/**
 * Shared currency formatters.
 *
 * IDR uses the `id-ID` locale so browsers render `Rp` instead of falling
 * back to `US$` (a known Chrome behavior when IDR is requested under
 * en-US with the default `symbol` display).
 */

/** Currencies that have no minor unit and must always render 0 decimals. */
const ZERO_DECIMAL_CURRENCIES = new Set([
  'IDR', 'JPY', 'KRW', 'VND', 'CLP', 'ISK', 'HUF', 'PYG', 'RWF', 'UGX', 'VUV', 'XAF', 'XOF', 'XPF',
]);

function localeFor(code: string): string | undefined {
  return code === 'IDR' ? 'id-ID' : undefined;
}

export function isZeroDecimalCurrency(currencyCode?: string | null): boolean {
  return ZERO_DECIMAL_CURRENCIES.has((currencyCode || '').toUpperCase());
}

/**
 * Service charge surfaces render whole units (0 decimals) per product decision.
 */
export function formatCurrencyAmount(amount: number, currencyCode?: string | null): string {
  const code = (currencyCode || 'IDR').toUpperCase();
  try {
    return new Intl.NumberFormat(localeFor(code), {
      style: 'currency',
      currency: code,
      currencyDisplay: 'symbol',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${code} ${Math.round(amount).toLocaleString()}`;
  }
}

/**
 * General money formatter used by payslip UI, PDFs and exports.
 * Uses 2 decimals, except for zero-decimal currencies (IDR, JPY, ...).
 */
export function formatMoney(amount: number, currencyCode?: string | null): string {
  const code = (currencyCode || 'USD').toUpperCase();
  const digits = isZeroDecimalCurrency(code) ? 0 : 2;
  try {
    return new Intl.NumberFormat(localeFor(code), {
      style: 'currency',
      currency: code,
      currencyDisplay: 'symbol',
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(Number(amount) || 0);
  } catch {
    return `${code} ${(Number(amount) || 0).toFixed(digits)}`;
  }
}

/** Plain numeric string for CSV cells (no symbol, currency-aware precision). */
export function formatMoneyPlain(amount: number, currencyCode?: string | null): string {
  const digits = isZeroDecimalCurrency(currencyCode) ? 0 : 2;
  return (Number(amount) || 0).toFixed(digits);
}
